/**
 * usePendingActions — React hook that surfaces the current offline-queue
 * state to the UI.
 *
 *   - On web: returns zero counts. The web bridge does not queue (Step 13).
 *   - On desktop: subscribes to `platformBridge.actionQueue.onChange` and
 *     refreshes both the stats and the pending list. Initial state is
 *     fetched once on mount.
 *
 * Used by `PendingSyncIndicator` and any operator-facing "show me what's
 * still waiting to sync" panels.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ActionQueueStats, PendingAction } from '@forgedas/shared-core';
import { platformBridge, isDesktop } from '../services/platformBridge';

const EMPTY_STATS: ActionQueueStats = {
  pending: 0,
  awaitingReauth: 0,
  failed: 0,
  flushing: false,
};

export interface UsePendingActions {
  stats: ActionQueueStats;
  pending: PendingAction[];
  /**
   * T83 — is the app actually connected? `true` unless we have positive evidence
   * otherwise. Two independent signals, and they answer different questions:
   *   - `navigator.onLine` knows whether this machine has a network interface. It
   *     does NOT know whether api.forgehalima.com is reachable, and it is famously
   *     optimistic (a captive portal reads as online).
   *   - `stats.online` is the queue's own probe of `<api origin>/health`, which is
   *     the question that actually matters — but it is `null` until something has
   *     been queued, because an empty queue never probes.
   * Either one saying "no" means offline. Neither saying "no" means online.
   */
  online: boolean;
  refresh: () => Promise<void>;
  retry: (id: string) => Promise<void>;
  discard: (id: string) => Promise<void>;
}

export function usePendingActions(): UsePendingActions {
  const [stats, setStats] = useState<ActionQueueStats>(EMPTY_STATS);
  const [pending, setPending] = useState<PendingAction[]>([]);
  const [navOnline, setNavOnline] = useState<boolean>(
    typeof navigator === 'undefined' || navigator.onLine !== false,
  );

  const refresh = useCallback(async () => {
    if (!isDesktop) {
      setStats(EMPTY_STATS);
      setPending([]);
      return;
    }
    try {
      const [nextStats, nextPending] = await Promise.all([
        platformBridge.actionQueue.stats(),
        platformBridge.actionQueue.list(),
      ]);
      setStats(nextStats);
      setPending(nextPending);
    } catch {
      // Queue not yet initialised, or IPC unavailable — leave previous state.
    }
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    let cancelled = false;

    void refresh();

    const unsubscribe = platformBridge.actionQueue.onChange((next) => {
      if (cancelled) return;
      setStats(next);
      // Pending list isn't included in the broadcast (it can be large), so
      // ask for the latest snapshot opportunistically.
      void platformBridge.actionQueue
        .list()
        .then((items) => {
          if (!cancelled) setPending(items);
        })
        .catch(() => {
          /* ignore */
        });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [refresh]);

  /* ══════════════════════════════════════════════════════════════════════════
     T84 — DRAIN THE MOMENT CONNECTIVITY RETURNS.

     Before this there was NO connectivity listener anywhere in the repo. The only
     reconnect signal was a blind 30s `setInterval` in the main process, so after
     the wifi came back a queued write could sit for 30s plus its own backoff (up
     to five minutes) before anyone tried again. To the operator that is
     indistinguishable from "it is not syncing" — which is exactly how the owner
     described it.

     Three triggers, because none of them is reliable alone: the `online` event is
     the direct signal but does not fire for a captive portal that later clears;
     window focus catches the "closed the laptop, opened it somewhere else" case;
     and visibility catches a background window being brought forward. All three
     collapse into one forced flush, and `flush()` is a no-op while one is running.
     ══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const goOnline = (): void => {
      setNavOnline(true);
      if (isDesktop) void platformBridge.actionQueue.flush().catch(() => { /* ignore */ });
    };
    const goOffline = (): void => setNavOnline(false);
    const onWake = (): void => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (document.visibilityState === 'hidden') return;
      if (isDesktop) void platformBridge.actionQueue.flush().catch(() => { /* ignore */ });
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, []);

  /* Positive evidence of being offline only. `stats.online === null` means the
     queue has never probed, which is NOT the same as unreachable — conflating the
     two is what pinned a slashed cloud on the header permanently. */
  const online = navOnline && stats.online !== false;

  const retry = useCallback(async (id: string) => {
    if (!isDesktop) return;
    await platformBridge.actionQueue.retry(id);
  }, []);

  const discard = useCallback(async (id: string) => {
    if (!isDesktop) return;
    await platformBridge.actionQueue.discard(id);
  }, []);

  return { stats, pending, online, refresh, retry, discard };
}
